import * as THREE from 'three';

import { createRendererOrExplain } from './contextGuard';
import { createContextLoss, type ContextLossHandle } from './contextLoss';
import { setSharedRenderer } from '../world/stage';

export interface PageRenderer {
  renderer: THREE.WebGLRenderer;
  /** The lost-context gate both pages share; `main.ts`'s cruise stage takes
   *  this one rather than building a second, so a page never installs more
   *  than one `webglcontextlost` listener. */
  contextLoss: ContextLossHandle;
}

/**
 * The one WebGLRenderer a page mounts: play's boot and explore's share this,
 * so the tone mapping, colour space and log-depth setup that make the Atlas
 * look the same in both cannot drift between them. The context-loss gate
 * (banner, exception event, frame-loop pause/resume) is built alongside it
 * for the same reason.
 *
 * Null when the context failed to acquire - `createRendererOrExplain` has
 * already painted its notice into `app`, and the caller's own guard
 * (`if (page) {`) is what keeps the rest of the page from running on it.
 */
export function createPageRenderer(app: HTMLElement, resume: () => void): PageRenderer | null {
  const renderer = createRendererOrExplain({
    antialias: true,
    powerPreference: 'high-performance',
    // One depth pass from a metre to an AU, so nothing has to decide which scale
    // band it is in before it can be drawn. Every custom ShaderMaterial in the
    // repo carries the logdepthbuf chunks for this.
    logarithmicDepthBuffer: true,
  }, app);
  if (!renderer) return null;
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
  renderer.setSize(window.innerWidth, window.innerHeight);
  renderer.toneMapping = THREE.ACESFilmicToneMapping;
  renderer.toneMappingExposure = 1.05;
  renderer.outputColorSpace = THREE.SRGBColorSpace;
  // First child, not last: the canvas is positioned (index.html/explore.html
  // `#app > canvas`), so DOM order is what keeps the HUD painting over the scene.
  app.prepend(renderer.domElement);
  // The Atlas draws through this renderer into a rect of this canvas; the handoff
  // goes through the stage registry rather than an import of main.ts.
  setSharedRenderer(renderer);
  const contextLoss = createContextLoss({ renderer, app, resume });
  return { renderer, contextLoss };
}
